import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getLiveDashboard, getAssignmentsByShift, sendHome, reassignEmployee } from '../api';

function Staffing() {
  const [locations, setLocations] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Get tenant_id from logged-in user
const user = JSON.parse(localStorage.getItem('user') || '{}');
const tenantId = user.tenant_id || 1;

  const fetchLocations = async () => {
    try {
      const response = await getLiveDashboard(tenantId);
      setLocations(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load staffing data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignments = async (shiftId) => {
    try {
      const response = await getAssignmentsByShift(shiftId);
      setAssignments(response.data);
    } catch (err) {
      console.error('Failed to load assignments:', err);
    }
  };

  useEffect(() => {
    fetchLocations();
    const interval = setInterval(fetchLocations, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedShift) {
      fetchAssignments(selectedShift);
    }
  }, [selectedShift]);

  const handleSendHome = async (assignmentId) => {
    if (window.confirm('Send this employee home?')) {
      try {
        await sendHome(assignmentId);
        fetchAssignments(selectedShift);
        fetchLocations();
      } catch (err) {
        alert('Failed to send employee home');
        console.error(err);
      }
    }
  };

  const handleReassign = async (assignmentId) => {
    const newShiftId = prompt('Enter the new shift ID:');
    if (newShiftId) {
      try {
        await reassignEmployee(assignmentId, parseInt(newShiftId));
        fetchAssignments(selectedShift);
        fetchLocations();
        alert('Employee reassigned successfully');
      } catch (err) {
        alert('Failed to reassign employee');
        console.error(err);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'working': return 'bg-green-100 text-green-800';
      case 'scheduled': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      case 'sent_home': return 'bg-orange-100 text-orange-800';
      case 'reassigned': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-gray-600">Loading staffing data...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <Link to="/" className="text-blue-500 hover:text-blue-700 mb-2 inline-block">&larr; Back to Home</Link>
            <h1 className="text-3xl font-bold text-gray-900">Staffing</h1>
            <p className="text-gray-600">Who's working today</p>
          </div>
          <button 
            onClick={fetchLocations}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Refresh
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Location List */}
          <div className="lg:col-span-1">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Locations</h2>
            <div className="space-y-2">
              {locations.map((location) => (
                <div
                  key={location.location_id}
                  onClick={() => location.shift_id && setSelectedShift(location.shift_id)}
                  className={`bg-white rounded-lg shadow p-4 cursor-pointer transition-all ${
                    selectedShift === location.shift_id ? 'ring-2 ring-blue-500' : 'hover:shadow-md'
                  } ${!location.shift_id ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium text-gray-900">{location.location_name}</h3>
                    <span className="text-lg font-semibold">
                      {location.current_employees || 0}
                      {location.target_headcount > 0 && (
                        <span className="text-gray-400 text-sm"> / {location.target_headcount}</span>
                      )}
                    </span>
                  </div>
                  {!location.shift_id && (
                    <p className="text-sm text-gray-400 mt-1">No shift today</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Assignment Details */}
          <div className="lg:col-span-2">
            {selectedShift ? (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Employees at {locations.find(l => l.shift_id === selectedShift)?.location_name}
                </h2>
                
                {assignments.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-6 text-center">
                    <p className="text-gray-500">No employees assigned to this shift yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {assignments.map((assignment) => (
                      <div key={assignment.id} className="bg-white rounded-lg shadow p-4">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-medium text-gray-900">
                              {assignment.first_name} {assignment.last_name}
                            </h3>
                            {assignment.phone && (
                              <p className="text-sm text-gray-500">{assignment.phone}</p>
                            )}
                            <div className="mt-2 flex gap-2 text-sm">
                              {assignment.clock_in && (
                                <span className="text-gray-600">
                                  In: {new Date(assignment.clock_in).toLocaleTimeString()}
                                </span>
                              )}
                              {assignment.clock_out && (
                                <span className="text-gray-600">
                                  Out: {new Date(assignment.clock_out).toLocaleTimeString()}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(assignment.status)}`}>
                              {assignment.status}
                            </span>
                            {assignment.status === 'working' && (
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleReassign(assignment.id)}
                                  className="text-blue-500 hover:text-blue-700 text-sm"
                                >
                                  Reassign
                                </button>
                                <button
                                  onClick={() => handleSendHome(assignment.id)}
                                  className="text-orange-500 hover:text-orange-700 text-sm"
                                >
                                  Send Home
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6 text-center">
                <p className="text-gray-500">Select a location to view employees</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Staffing;